// assets/js/tickets-auth-verify.js
/**
 * Inline 6-digit email confirmation for the ticketing landing page's own
 * sign-in dialog (tickets.html and its six localized copies).
 *
 * Why not the shared auth modal (assets/js/auth/auth-modal.js): that one is
 * built on bootstrap.Modal, and the ticketing landing page deliberately ships
 * without Bootstrap's CSS or JS. Adopting it would put ~110KB of framework on
 * a marketing page to render one dialog. So this is the same flow, grafted onto
 * the dialog that page already has.
 *
 * VERIFY BEFORE CREATE: the address answers the code first, and the account is
 * created only after it does. An unconfirmed account cannot exist on this path,
 * so closing the code step costs the visitor nothing but a retyped password.
 *
 * ⚠️ The strings below are COPIED from AUTH_MODAL_TRANSLATIONS in
 * auth-modal.js (generated, not retyped). Change one, change both.
 *
 * The page calls exactly three things:
 *   beginSignup({email, password, name})  from the sign-up form's submit
 *   canClose(source)                      from closeModal(), to refuse
 *                                         everything but the X
 *   onClosed()                            after the dialog actually closed
 */
(function () {
  'use strict';

  const STRINGS = {
  en: {
    verifyTitle: "Confirm your email",
    verifySent: "We sent a 6-digit code to <strong>{email}</strong>. The code is in the subject line — you may not even need to open the email.",
    verifySpam: "No code? Check your spam or junk folder, and Gmail's Promotions tab.",
    verifyCodeLabel: "6-digit code",
    verifyBtn: "Confirm and continue",
    verifyResend: "Resend code",
    verifyResendIn: "Resend in {n}s",
    verifyResent: "New code sent.",
    verifyBadCode: "That code is not right. Check it and try again.",
    verifyOpenGmail: "Open Gmail",
    verifyOpenOutlook: "Open Outlook",
    verifyDone: "Email confirmed — your account is ready.",
    verifyClaimFailed: "Your account is created and you're signed in, but we couldn't finish confirming your email. If it's needed, we'll ask you to confirm it again.",
    verifyDismiss: "Close this and we won't create the account.",
  },
  he: {
    verifyTitle: "אישור כתובת האימייל",
    verifySent: "שלחנו קוד בן 6 ספרות אל <strong>{email}</strong>. הקוד מופיע בשורת הנושא — ייתכן שלא תצטרכו אפילו לפתוח את המייל.",
    verifySpam: "לא קיבלתם קוד? בדקו בתיקיית הספאם, וגם בלשונית קידומי מכירות בג'מייל.",
    verifyCodeLabel: "קוד בן 6 ספרות",
    verifyBtn: "אישור והמשך",
    verifyResend: "שליחת קוד מחדש",
    verifyResendIn: "שליחה מחדש בעוד {n} שניות",
    verifyResent: "נשלח קוד חדש.",
    verifyBadCode: "הקוד שגוי. בדקו ונסו שוב.",
    verifyOpenGmail: "פתיחת Gmail",
    verifyOpenOutlook: "פתיחת Outlook",
    verifyDone: "האימייל אומת — החשבון מוכן.",
    verifyClaimFailed: "החשבון נוצר ואתם מחוברים, אבל לא הצלחנו להשלים את אימות האימייל. אם יהיה צורך, נבקש מכם לאמת אותו שוב.",
    verifyDismiss: "אם תסגרו את החלון, לא ניצור את החשבון.",
  },
  ar: {
    verifyTitle: "تأكيد بريدك الإلكتروني",
    verifySent: "أرسلنا رمزاً من 6 أرقام إلى <strong>{email}</strong>. الرمز موجود في سطر الموضوع — قد لا تحتاج حتى إلى فتح الرسالة.",
    verifySpam: "لم يصلك الرمز؟ تحقق من مجلد الرسائل غير المرغوب فيها، ومن تبويب العروض الترويجية في Gmail.",
    verifyCodeLabel: "رمز من 6 أرقام",
    verifyBtn: "تأكيد ومتابعة",
    verifyResend: "إعادة إرسال الرمز",
    verifyResendIn: "إعادة الإرسال خلال {n} ثانية",
    verifyResent: "تم إرسال رمز جديد.",
    verifyBadCode: "الرمز غير صحيح. تحقق منه وحاول مرة أخرى.",
    verifyOpenGmail: "فتح Gmail",
    verifyOpenOutlook: "فتح Outlook",
    verifyDone: "تم تأكيد البريد الإلكتروني — حسابك جاهز.",
    verifyClaimFailed: "تم إنشاء حسابك وتسجيل دخولك، لكننا لم نتمكن من إكمال تأكيد بريدك الإلكتروني. إذا لزم الأمر، سنطلب منك تأكيده مرة أخرى.",
    verifyDismiss: "إذا أغلقت هذه النافذة، فلن نُنشئ الحساب.",
  },
  es: {
    verifyTitle: "Confirma tu correo",
    verifySent: "Enviamos un código de 6 dígitos a <strong>{email}</strong>. El código está en el asunto — puede que ni necesites abrir el correo.",
    verifySpam: "¿No ves el código? Revisa tu carpeta de spam o correo no deseado, y la pestaña Promociones de Gmail.",
    verifyCodeLabel: "Código de 6 dígitos",
    verifyBtn: "Confirmar y continuar",
    verifyResend: "Reenviar código",
    verifyResendIn: "Reenviar en {n}s",
    verifyResent: "Nuevo código enviado.",
    verifyBadCode: "Ese código no es correcto. Revísalo e inténtalo de nuevo.",
    verifyOpenGmail: "Abrir Gmail",
    verifyOpenOutlook: "Abrir Outlook",
    verifyDone: "Correo confirmado: tu cuenta está lista.",
    verifyClaimFailed: "Tu cuenta está creada y has iniciado sesión, pero no pudimos terminar de confirmar tu correo. Si hace falta, te pediremos que lo confirmes de nuevo.",
    verifyDismiss: "Si cierras esta ventana, no crearemos la cuenta.",
  },
  fr: {
    verifyTitle: "Confirmez votre e-mail",
    verifySent: "Nous avons envoyé un code à 6 chiffres à <strong>{email}</strong>. Le code est dans l'objet — vous n'aurez peut-être même pas besoin d'ouvrir l'e-mail.",
    verifySpam: "Pas de code ? Vérifiez votre dossier spam ou courrier indésirable, et l'onglet Promotions de Gmail.",
    verifyCodeLabel: "Code à 6 chiffres",
    verifyBtn: "Confirmer et continuer",
    verifyResend: "Renvoyer le code",
    verifyResendIn: "Renvoyer dans {n} s",
    verifyResent: "Nouveau code envoyé.",
    verifyBadCode: "Ce code est incorrect. Vérifiez-le et réessayez.",
    verifyOpenGmail: "Ouvrir Gmail",
    verifyOpenOutlook: "Ouvrir Outlook",
    verifyDone: "E-mail confirmé — votre compte est prêt.",
    verifyClaimFailed: "Votre compte est créé et vous êtes connecté, mais nous n'avons pas pu finir de confirmer votre e-mail. Si besoin, nous vous demanderons de le confirmer à nouveau.",
    verifyDismiss: "Si vous fermez cette fenêtre, le compte ne sera pas créé.",
  },
  pt: {
    verifyTitle: "Confirme seu e-mail",
    verifySent: "Enviamos um código de 6 dígitos para <strong>{email}</strong>. O código está no assunto — talvez você nem precise abrir o e-mail.",
    verifySpam: "Sem código? Verifique sua pasta de spam ou lixo eletrônico, e a aba Promoções do Gmail.",
    verifyCodeLabel: "Código de 6 dígitos",
    verifyBtn: "Confirmar e continuar",
    verifyResend: "Reenviar código",
    verifyResendIn: "Reenviar em {n}s",
    verifyResent: "Novo código enviado.",
    verifyBadCode: "Esse código não está certo. Verifique e tente novamente.",
    verifyOpenGmail: "Abrir Gmail",
    verifyOpenOutlook: "Abrir Outlook",
    verifyDone: "E-mail confirmado — sua conta está pronta.",
    verifyClaimFailed: "Sua conta foi criada e você está conectado, mas não conseguimos concluir a confirmação do seu e-mail. Se for preciso, pediremos que você confirme novamente.",
    verifyDismiss: "Se você fechar esta janela, não criaremos a conta.",
  },
  de: {
    verifyTitle: "E-Mail-Adresse bestätigen",
    verifySent: "Wir haben einen 6-stelligen Code an <strong>{email}</strong> gesendet. Der Code steht in der Betreffzeile — Sie müssen die E-Mail vielleicht gar nicht öffnen.",
    verifySpam: "Kein Code? Sehen Sie im Spam- oder Junk-Ordner nach — und im Gmail-Tab Werbung.",
    verifyCodeLabel: "6-stelliger Code",
    verifyBtn: "Bestätigen und fortfahren",
    verifyResend: "Code erneut senden",
    verifyResendIn: "Erneut senden in {n}s",
    verifyResent: "Neuer Code gesendet.",
    verifyBadCode: "Dieser Code stimmt nicht. Bitte prüfen und erneut versuchen.",
    verifyOpenGmail: "Gmail öffnen",
    verifyOpenOutlook: "Outlook öffnen",
    verifyDone: "E-Mail bestätigt — Ihr Konto ist bereit.",
    verifyClaimFailed: "Ihr Konto ist erstellt und Sie sind angemeldet, aber wir konnten Ihre E-Mail nicht fertig bestätigen. Falls nötig, bitten wir Sie, sie erneut zu bestätigen.",
    verifyDismiss: "Wenn Sie dieses Fenster schließen, wird das Konto nicht erstellt.",
  },  };

  function pickLang() {
    const l = (document.documentElement.lang || 'en').toLowerCase().split('-')[0];
    return STRINGS[l] ? l : 'en';
  }

  const t = STRINGS[pickLang()];

  // active:  the code step is on screen
  // done:    the code was accepted and the account exists
  // pending: {email, password, name} held in memory only, never stored
  const state = { active: false, done: false, pending: null, resendTimer: null };

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function el(id) { return document.getElementById(id); }
  function panel() { return el('ticketsVerifyStep'); }

  function injectStyles() {
    if (el('ticketsVerifyStyles')) return;
    const s = document.createElement('style');
    s.id = 'ticketsVerifyStyles';
    s.textContent = [
      '#ticketsVerifyStep{display:none;flex-direction:column;gap:0.55rem}',
      '#ticketsVerifyStep .tv-sent{font-size:0.9rem;color:var(--text-dim,#6b6b6b);margin:0 0 0.35rem;line-height:1.5}',
      '#ticketsVerifyCode{font-size:1.45rem;letter-spacing:0.4em;text-align:center;font-variant-numeric:tabular-nums}',
      '#ticketsVerifyStep .tv-spam{font-size:0.82rem;color:var(--text-dim,#6b6b6b);text-align:center;margin:0.35rem 0 0}',
      '#ticketsVerifyStep .tv-row{display:flex;flex-wrap:wrap;justify-content:center;gap:0.75rem;margin-top:0.2rem}',
      '#ticketsVerifyStep .tv-dismiss{font-size:0.78rem;color:var(--text-dim,#8a8a8a);text-align:center;margin:0.5rem 0 0}'
    ].join('');
    document.head.appendChild(s);
  }

  function build() {
    if (panel()) return panel();
    const host = document.querySelector('.signin-modal');
    if (!host) return null;
    injectStyles();
    const div = document.createElement('div');
    div.id = 'ticketsVerifyStep';
    div.className = 'signin-form';
    div.innerHTML =
      '<p class="tv-sent" id="ticketsVerifySent"></p>' +
      '<label class="signin-label" for="ticketsVerifyCode">' + esc(t.verifyCodeLabel) + '</label>' +
      '<input type="text" id="ticketsVerifyCode" class="signin-input" inputmode="numeric" ' +
        'autocomplete="one-time-code" maxlength="6" placeholder="000000">' +
      '<button type="button" class="btn-gold signin-submit" id="ticketsVerifySubmit">' + esc(t.verifyBtn) + '</button>' +
      '<p class="tv-spam">' + esc(t.verifySpam) + '</p>' +
      '<div class="tv-row">' +
        '<a class="signin-link" target="_blank" rel="noopener" ' +
          'href="https://mail.google.com/mail/u/0/#search/in%3Aanywhere+from%3Aaccounts.quikrsvp.com">' + esc(t.verifyOpenGmail) + '</a>' +
        '<a class="signin-link" target="_blank" rel="noopener" href="https://outlook.live.com/mail/0/">' + esc(t.verifyOpenOutlook) + '</a>' +
        '<button type="button" class="signin-link" id="ticketsVerifyResend">' + esc(t.verifyResend) + '</button>' +
      '</div>' +
      '<p class="tv-dismiss">' + esc(t.verifyDismiss) + '</p>';
    host.appendChild(div);

    el('ticketsVerifySubmit').addEventListener('click', submit);
    el('ticketsVerifyResend').addEventListener('click', resend);
    const input = el('ticketsVerifyCode');
    input.addEventListener('input', function () {
      const cleaned = input.value.replace(/\D/g, '').slice(0, 6);
      if (cleaned !== input.value) input.value = cleaned;
      if (cleaned.length === 6) submit();
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); submit(); }
    });
    return div;
  }

  function message(msg, isError) {
    const box = el('signinError');
    if (!box) return;
    box.textContent = msg || '';
    box.style.display = msg ? 'block' : 'none';
    box.style.color = isError ? '' : 'var(--gold-deep, #7a6231)';
  }

  /** Ask for a code by ADDRESS — there is no account to authenticate as yet. */
  async function requestCode(email) {
    const res = await fetch('/api/email-automation/signup/request-code', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, lang: document.documentElement.lang || 'en' })
    });
    const data = await res.json().catch(function () { return {}; });
    if (data.accountExists) return { accountExists: true };
    if (!res.ok || data.success === false) {
      throw new Error(data.error || 'We could not send the code. Please try again.');
    }
    return { sent: true };
  }

  async function submit() {
    const input = el('ticketsVerifyCode');
    const code = ((input && input.value) || '').trim();
    if (!/^\d{6}$/.test(code)) { message(t.verifyBadCode, true); return; }

    const pending = state.pending;
    if (!pending) return;

    const btn = el('ticketsVerifySubmit');
    if (btn) btn.disabled = true;
    try {
      const confirm = await fetch('/api/email-automation/signup/confirm-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: pending.email, code })
      });
      const confirmData = await confirm.json().catch(function () { return {}; });
      if (!confirm.ok || !confirmData.claimToken) {
        message(confirmData.error || t.verifyBadCode, true);
        return;
      }

      // The address has answered — now the account may exist.
      const cred = await window.firebaseAuth.createUserWithEmailAndPassword(pending.email, pending.password);
      if (pending.name && cred.user) {
        try { await cred.user.updateProfile({ displayName: pending.name }); } catch (e) { /* cosmetic */ }
      }

      const token = await cred.user.getIdToken();
      // The claim is what marks the address verified and turns on the
      // owner's pending links. Its token stays valid for 15 minutes and a
      // failed attempt does not spend it, so one retry is safe; and a claim
      // that still fails must not end on "verified" (the account exists and
      // is signed in, and the Share tab's activation card can finish it).
      const claimOnce = () => fetch('/api/email-automation/signup/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ claimToken: confirmData.claimToken })
      }).then(function (r) { return r.ok; }, function () { return false; });
      const claimed = (await claimOnce()) || (await claimOnce());
      if (!claimed) {
        state.done = true;
        state.pending = null;
        message(t.verifyClaimFailed, true);
        return;
      }
      // Refresh so the email_verified claim is live for firestore.rules before
      // the visitor's first write. reload() first: getIdToken(true) renews the
      // claims but not currentUser.emailVerified, which client code reads
      // directly (see auth-modal.js, 2026-09-16).
      try { await cred.user.reload(); } catch (e) { /* non-fatal */ }
      try { await cred.user.getIdToken(true); } catch (e) { /* lands on next refresh */ }

      state.done = true;
      state.pending = null;
      message(t.verifyDone, false);
      setTimeout(closeDialog, 1200);
    } catch (e) {
      message((e && e.code === 'auth/email-already-in-use')
        ? 'That email already has an account — switch to Sign in.'
        : ((e && e.message) || t.verifyBadCode), true);
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  async function resend() {
    const btn = el('ticketsVerifyResend');
    if (!btn || btn.disabled) return;
    if (!state.pending) return;
    try { await requestCode(state.pending.email); } catch (e) { message(e.message, true); return; }
    message(t.verifyResent, false);

    let left = 30;
    btn.disabled = true;
    const label = function () { btn.textContent = String(t.verifyResendIn).replace('{n}', left); };
    label();
    clearInterval(state.resendTimer);
    state.resendTimer = setInterval(function () {
      left -= 1;
      if (left <= 0) {
        clearInterval(state.resendTimer);
        btn.disabled = false;
        btn.textContent = t.verifyResend;
      } else { label(); }
    }, 1000);
  }

  function closeDialog() {
    const backdrop = el('signinBackdrop');
    if (backdrop) backdrop.classList.remove('show');
    document.body.style.overflow = '';
    reset();
  }

  function reset() {
    state.active = false;
    state.pending = null;
    clearInterval(state.resendTimer);
    const step = panel();
    if (step) step.style.display = 'none';
    const signin = el('signinForm');
    const signup = el('signupForm');
    if (signin) signin.style.display = 'flex';
    if (signup) signup.style.display = 'none';
  }

  window.ticketsVerify = {
    /**
     * Owns everything after the sign-up form is submitted. Resolves
     * { accountExists: true } if the address is already somebody's — the page
     * shows that as "switch to Sign in" — and otherwise shows the code step.
     * Throws on a send failure so the page can render it with its own styling.
     */
    beginSignup: async function (details) {
      const email = String((details && details.email) || '').trim();
      const password = (details && details.password) || '';
      const name = String((details && details.name) || '').trim() || email.split('@')[0];

      const outcome = await requestCode(email);
      if (outcome.accountExists) return { accountExists: true };
      if (!build()) throw new Error('Please reload the page and try again.');

      // Held in memory for the length of the dialog and nowhere else.
      state.pending = { email, password, name };
      state.active = true;
      state.done = false;

      const signin = el('signinForm');
      const signup = el('signupForm');
      if (signin) signin.style.display = 'none';
      if (signup) signup.style.display = 'none';
      panel().style.display = 'flex';

      const title = el('signinTitle');
      const sub = el('signinSub');
      if (title) title.textContent = t.verifyTitle;
      if (sub) sub.textContent = '';
      message('', false);

      const sent = el('ticketsVerifySent');
      if (sent) sent.innerHTML = String(t.verifySent).replace('{email}', esc(email));
      const input = el('ticketsVerifyCode');
      if (input) { input.value = ''; setTimeout(function () { input.focus(); }, 150); }

      return { sent: true };
    },

    /**
     * The code step is dismissible by the X and nothing else: a stray backdrop
     * tap or an Escape while reading the code out of another app must not throw
     * it away, and the sign-in that account creation fires must not either.
     */
    canClose: function (source) {
      if (!state.active) return true;
      return source === 'x';
    },

    /** Nothing to undo — no account was ever created on this path. */
    onClosed: function () { reset(); }
  };
})();
